// resolve-json-seed-value.ts — PSTUDIO-4415

const INDENT = '  ';

/**
 * A layout writes an indent per bracket, comma and colon, so its size grows with
 * depth squared. Measured on `'['.repeat(d) + ']'.repeat(d)`: depth 1000 → 2 KB
 * in, 2 MB out; depth 5000 → 10 KB in, 50 MB out; depth 20000 → a `RangeError`
 * thrown from inside the render. The cell text can come from an import, a paste
 * or a coworker, so the layout is only done where it stays small: real JSON is
 * a few levels deep, and at these limits the output stays around 10 MB at most.
 * Measured on Node 24 at depth 32, in three independent runs: about 200 000
 * characters of `1,` → 6.5–6.7 MB in 13–146 ms; 196 065 characters of `[1],`
 * → 9.56 MB in 24–107 ms (first run of each is cold). The layout is recomputed
 * whenever the stored value changes while the cell is unedited, so such a cell
 * costs that much per change; nothing is written by it.
 */
export const MAX_BEAUTIFY_DEPTH = 32;
export const MAX_BEAUTIFY_LENGTH = 200_000;

/** The key of the cell the user last typed into in the panel's JSON editor. */
export const editedCellKey = (rowId: string, columnId: string): string =>
  `${rowId}:${columnId}`;

/**
 * What the key becomes when the selection changes. Selecting any other cell, or
 * nothing, releases it; the cell stays "edited" for as long as it is the one on
 * show. It is deliberately NOT released by a commit: a commit also happens when
 * the window loses focus, with the caret still in the editor, and re-laying the
 * text out then would replace the document under that caret.
 */
export const keyAfterSelectionChange = (
  current: string | null,
  rowId: string | undefined,
  columnId: string | undefined
): string | null =>
  rowId && columnId && current === editedCellKey(rowId, columnId)
    ? current
    : null;

/** True only for text that parses to an object or an array. */
const isJsonContainer = (text: string): boolean => {
  const first = text.trimStart()[0];
  if (first !== '{' && first !== '[') return false;
  try {
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === 'object' && parsed !== null;
  } catch {
    return false;
  }
};

/**
 * Re-lays out VALID JSON text: one field or item per line, two-space indents,
 * `"key": value`, empty `{}` / `[]` kept on one line — the layout of
 * `JSON.stringify(value, null, 2)`.
 *
 * It works on the TEXT, not on a parsed value, because the result is what the
 * editor holds, and the first edit a user makes saves the editor's text. Going
 * through `JSON.parse` + `JSON.stringify` would change what is stored on that
 * first edit: integers past 2^53 lose digits, `1.0` and `1e3` become `1` and
 * `1000`, a repeated key collapses to its last value, `é` becomes `é`, and
 * integer-like keys move in front of the others. Here only whitespace outside
 * string literals is added or removed, and every other character is copied.
 *
 * The caller passes valid JSON (`isJsonContainer`); on invalid text the layout
 * is unspecified. Returns `null` when the text nests deeper than
 * `MAX_BEAUTIFY_DEPTH`, before any large output exists.
 */
export const beautifyJsonText = (raw: string): string | null => {
  let out = '';
  let depth = 0;
  let inString = false;

  const nextNonSpace = (from: number): number => {
    let j = from;
    while (j < raw.length && /\s/.test(raw[j])) j += 1;
    return j;
  };

  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i];

    if (inString) {
      out += ch;
      if (ch === '\\') {
        i += 1;
        out += raw[i] ?? '';
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      out += ch;
    } else if (ch === '{' || ch === '[') {
      const closer = ch === '{' ? '}' : ']';
      const next = nextNonSpace(i + 1);
      if (raw[next] === closer) {
        out += ch + closer;
        i = next;
      } else {
        depth += 1;
        if (depth > MAX_BEAUTIFY_DEPTH) return null;
        out += `${ch}\n${INDENT.repeat(depth)}`;
      }
    } else if (ch === '}' || ch === ']') {
      depth -= 1;
      out += `\n${INDENT.repeat(depth)}${ch}`;
    } else if (ch === ',') {
      out += `,\n${INDENT.repeat(depth)}`;
    } else if (ch === ':') {
      out += ': ';
    } else if (!/\s/.test(ch)) {
      out += ch;
    }
  }

  return out;
};

export interface ResolveJsonSeedValueArgs {
  /** The raw string the panel would otherwise hand to the JSON editor. */
  value: string;
  /**
   * Whether the user has typed into THIS cell's panel editor. Once they have,
   * the text is whatever the editor emitted and must come back unchanged.
   */
  userEdited: boolean;
}

/**
 * The text the panel's JSON editor SHOWS for a string value.
 *
 * A JSON column can hold its content as a string — an import or a paste keeps
 * whatever spacing it arrived with, often one long line — while a parsed object
 * is already pretty-printed by `cellDisplayValue`. This gives the string case
 * the same layout. It is a DISPLAY transform: the stored value is rewritten by
 * nothing here, only by an edit the user makes afterwards, which saves the
 * editor's text as it stands — laid out. An edit that nets back to exactly
 * this layout is not saved (`resolvePanelCommitAction`, `shownBaseline`).
 * PSTUDIO-1092 reads "Do not change the JSON automatically, there should be a
 * 1 click CTA to apply the changes": the stored value is not changed
 * automatically here, but the panel shows a different view of it from the
 * grid's single line, and there is no CTA.
 *
 * Two inputs it leaves alone, because the editor is controlled and a string
 * different from the one it just emitted replaces its document under the caret:
 * - a cell the user has edited — the flag, not a comparison with the original,
 *   decides, so typing or pasting the original text back is still an edit;
 * - text that is not an object or array: half-typed JSON, scalars, plain text.
 */
export const resolveJsonSeedValue = ({
  value,
  userEdited,
}: ResolveJsonSeedValueArgs): string => {
  if (userEdited) return value;
  if (value.length > MAX_BEAUTIFY_LENGTH) return value;
  if (!isJsonContainer(value)) return value;
  // Called from a render, so a failure here would take the panel down; the text
  // as stored is always a correct thing to show instead. No input within the
  // limits is known to throw — the test forces the failure by stubbing
  // `String.prototype.repeat`.
  try {
    return beautifyJsonText(value) ?? value;
  } catch (error) {
    console.error('Failed to lay out JSON for display (PSTUDIO-4415)', error);
    return value;
  }
};

/**
 * The text a paste into an empty or fully replaced JSON editor becomes: the same
 * layout the panel shows for a stored string, so a pasted one-liner is not left
 * compact until the cell is selected again. Anything that is not an object or an
 * array comes back unchanged.
 */
export const layoutPastedJson = (pasted: string): string =>
  resolveJsonSeedValue({ value: pasted, userEdited: false });
